import { useEffect, useRef, useState } from 'react';
import { Camera, Pencil } from '@phosphor-icons/react';

import { WizardStepHeader } from '../../wizard';
import pt from '../../../locales/pt';
import { formatEUR } from '../../../utils/money';
import { formatMonthLabel } from '../../../utils/goalWizardUtils';

export function SmartGoalVisionStep({
  imageFile,
  initialUrl,
  onChange,
  goalName,
  isChecklist,
  amountValue,
  monthValue,
}) {
  const copy = pt.goals.wizard.vision;
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(initialUrl || null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(initialUrl || null);
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile, initialUrl]);

  const handlePick = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return;
    if (!file.type.startsWith('image/')) return;
    onChange?.(file);
  };

  const metaLine = isChecklist
    ? formatMonthLabel(monthValue)
    : `${formatEUR(amountValue || 0).replace(/\s*€$/, '').trim()} € · ${formatMonthLabel(monthValue)}`;

  return (
    <div className="flex flex-col gap-[15px] pt-2">
      <WizardStepHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0])}
      />

      {previewUrl ? (
        <div className="relative h-[300px] overflow-hidden rounded-[20px] shadow-[0_16px_32px_-18px_rgba(28,25,23,0.5)]">
          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          <div
            className="absolute inset-0 bg-gradient-to-t from-[rgba(28,25,23,0.62)] to-transparent"
            aria-hidden
          />
          <div className="absolute bottom-3.5 left-4 right-4">
            <p className="font-display text-[19px] font-semibold tracking-tight text-white">
              {goalName || '—'}
            </p>
            <p className="mt-0.5 text-[13px] text-white/85">{metaLine}</p>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-xl bg-white/92 px-3 py-2 text-xs font-semibold text-stone-700"
          >
            <Pencil className="h-[15px] w-[15px]" aria-hidden />
            {copy.swap}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-[300px] w-full flex-col items-center justify-center gap-3 rounded-[20px] border-2 border-dashed border-sage-300 bg-[#F8FAF5] px-6 text-center dark:border-sage-700 dark:bg-sage-950/30"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sage-100 text-sage-700 dark:bg-sage-900 dark:text-sage-300">
            <Camera className="h-[30px] w-[30px]" weight="regular" aria-hidden />
          </span>
          <span>
            <span className="block font-display text-base font-semibold text-foreground">
              {copy.pickTitle}
            </span>
            <span className="mt-1 block text-[13px] leading-snug text-muted-foreground">
              {copy.pickHint}
            </span>
          </span>
        </button>
      )}
    </div>
  );
}

export default SmartGoalVisionStep;
